import { useDetailsDrawer } from '@src/components/DetailsDrawer/useDetailsDrawer';
import { parseSection } from '@src/components/NavigationSidebar/parseSection';
import { getSpaceBasePath, getSpacePathPrefix } from '@src/components/NavigationSidebar/spacesNavPath';
import { selectView } from '@src/components/OMS/OMSSlice';
import { OMSView } from '@src/components/OMS/OMSView';
import { isCurrentRouteCustomerOrderDetails } from '@src/containers/Routes/routes';
import { useAppLayoutForRouteParams } from '@src/hooks/useAppLayoutId';
import { toNavGroupName } from '@src/hooks/useSpacesNavItems';
import { useAppStateSelector, useAppStateStore } from '@src/providers/AppStateProvider/AppStateProviderHooks';
import { CustomerOrder, Order } from '@talos/kyoko';
import { useEffect, useRef } from 'react';
import { matchPath, useLocation, useNavigate, useParams } from 'react-router-dom';
import { TradingPlaceholder } from './TradingPlaceholder';
import { useMobileTradingNavItems } from './useMobileTradingNavItems';
import { getRedirectToDefaultChildPath } from './utils/getRedirectToDefaultChildPath';

/**
 * Handles `/{section}/{group?}/{layoutId}/order|customer-order/{orderId}/...` on mobile.
 * Opens the Order Analytics modal sheet and navigates back to the
 * default trading panel when the sheet is closed.
 */
export function MobileOrderDetailsRoute() {
  const { orderID, layoutId } = useParams<{ orderID?: string; layoutId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const routeMatch = matchPath({ path: '/:section' }, location.pathname);
  const layoutSection = parseSection(routeMatch?.params.section) ?? 'trading';
  const { openDetailsDrawer, closeDetailsDrawer, isOpen } = useDetailsDrawer();
  const openedView = useAppStateSelector(selectView);
  const store = useAppStateStore();
  const tradingNavItems = useMobileTradingNavItems();
  const layout = useAppLayoutForRouteParams(layoutSection);
  const wasDrawerOpenRef = useRef(false);
  const isCustomerOrder = isCurrentRouteCustomerOrderDetails(location.pathname);

  // Open the Order Analytics modal sheet when this route is visited (or when the order id / type changes).
  useEffect(() => {
    if (orderID == null) {
      return;
    }

    const entity = isCustomerOrder
      ? new CustomerOrder({ OrderID: orderID } as CustomerOrder)
      : new Order({ OrderID: orderID } as Order);

    openDetailsDrawer({ entity });
  }, [orderID, isCustomerOrder, openDetailsDrawer]);

  // Leave the order-details URL once the drawer session ends. If the drawer was replaced by another
  // OMS view (e.g. Modify → NewOrderForm), wait until that view closes before navigating.
  useEffect(() => {
    if (isOpen) {
      wasDrawerOpenRef.current = true;
      return;
    }

    if (!wasDrawerOpenRef.current || openedView != null) {
      return;
    }

    wasDrawerOpenRef.current = false;

    const layoutBasePath =
      layout != null
        ? getSpaceBasePath(layoutSection, layout.group ? toNavGroupName(layout.group) : undefined, layout.id)
        : layoutId != null
          ? (getSpacePathPrefix(location.pathname, layoutSection, layoutId) ?? `/${layoutSection}/${layoutId}`)
          : `/${layoutSection}`;
    const defaultPath = getRedirectToDefaultChildPath(tradingNavItems, layoutBasePath) ?? layoutBasePath;
    navigate(defaultPath, { replace: true });
  }, [isOpen, openedView, layout, layoutId, layoutSection, location.pathname, navigate, tradingNavItems]);

  // Close the drawer on unmount only when it is still the active view, so we do not tear down a
  // replacement view such as the order form opened via Modify.
  useEffect(() => {
    return () => {
      if (selectView(store.getState()) === OMSView.DetailsDrawer) {
        closeDetailsDrawer();
      }
    };
  }, [closeDetailsDrawer, store]);

  return <TradingPlaceholder label="Trading" />;
}
